<template>
  <div class="calendar-container">
    <!-- Header with navigation -->
    <div class="row justify-between items-center q-pb-md">
      <q-btn flat round icon="chevron_left" @click="prevMonth" />

      <div class="text-weight-bold" :class="$q.screen.gt.sm ? 'text-h6' : 'body2'">
        {{ monthName }} {{ currentYear }}
      </div>

      <q-btn flat round icon="chevron_right" @click="nextMonth" />
    </div>

    <!-- Weekday labels -->
    <div class="row text-center text-caption text-grey-7 q-mb-sm">
      <div class="col" v-for="d in weekdays" :key="d">{{ d }}</div>
    </div>

    <!-- Days Grid -->
    <div class="day-grid">
      <div
        v-for="(day, index) in calendarDays"
        :key="index"
        :class="['day-cell', { highlighted: day.inRange }]"
      >
        <span v-if="day.date">{{ day.dayNum }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps({
  start_date: { type: Date, required: true },
  end_date: { type: Date, required: true },
})

// Access start and end date directly (already Date objects)
const start = computed(() => props.start_date)
const end = computed(() => props.end_date)

const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

// === CURRENTLY DISPLAYED MONTH ===
const currentYear = ref(start.value?.getFullYear())
const currentMonth = ref(start.value.getMonth()) // 0–11

// Month name
const monthName = computed(() => {
  return new Date(currentYear.value, currentMonth.value)
    .toLocaleString('en-US', { month: 'long' })
    .toUpperCase()
})

// Navigation
function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
}

// Calendar grid generation
const calendarDays = computed(() => {
  const first = new Date(currentYear.value, currentMonth.value, 1)
  const last = new Date(currentYear.value, currentMonth.value + 1, 0)

  const firstWeekday = first.getDay()
  const days = []

  // Blank placeholders before 1st
  for (let i = 0; i < firstWeekday; i++) {
    days.push({ date: null, inRange: false })
  }

  // Actual month days
  for (let d = 1; d <= last.getDate(); d++) {
    const cur = new Date(Date.UTC(currentYear.value, currentMonth.value, d))
    const inRange = cur >= start.value && cur <= end.value

    days.push({
      date: cur,
      dayNum: d,
      inRange,
    })
  }

  return days
})
</script>

<style scoped lang="scss">
.calendar-container {
  background: white;
  border-radius: 12px;
  padding: 16px;
}

.day-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.day-cell {
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.highlighted {
  background: $info;
  border-radius: 12px;
}
</style>
